import { mobileDetected } from 'Shared/utils/mobile-detect';
import { useContext, useEffect, memo, useMemo, ChangeEventHandler } from 'react';
import { useClusteredDataContext } from 'Guide/Schematics/Diagram/Providers/clusteredData';
import { ZoomContext } from 'Guide/Schematics/Diagram/Providers/d3Zoomable';
import { SearchInput } from 'Shared/search_input';
import { PartsList, PartListContainer } from './parts_list';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';
import { KeypressContext } from '../Providers/keypressContext';
import { TooltipContext } from '../Providers/tooltipContext';
import { scaleAndCenterOnDataPoint } from 'Guide/Schematics/Diagram/Logic/scaleAndCenterOn';
import { useSearchContext } from 'Guide/Schematics/Diagram/Providers/searchContext';
import { fromWiki } from '../Generic/fromWiki';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { useDotsVisibility } from '../Providers/dotsVisibilityContext';
import { _js } from '@ifixit/localize';
import { Switch } from '@ifixit/ui/components/switch';
import { Label } from '@ifixit/ui/components/label';

function Part({
   item,
   onClose,
}: {
   item: iFixitHotSpotItem;
   onClose: MinimalDisclosure['onClose'];
}) {
   const clusteredDataContext = useClusteredDataContext<iFixitHotSpotItem>();
   const zoomContext = useContext(ZoomContext);
   const tooltipContext = useContext(TooltipContext);
   const { setDotsVisible } = useDotsVisibility();

   const onClick = () => {
      // The popup is anchored to a dot, so picking a part has to bring the dots
      // back, otherwise the tooltip has nothing to render against.
      setDotsVisible(true);
      // try tooltip opening before we tween
      tooltipContext.setOpenDataPointId(item.datapointid);
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME: Promises must be awaited, end with...
      scaleAndCenterOnDataPoint(item.datapointid, clusteredDataContext, zoomContext).then(() => {
         //ensure tooltip is open after tween
         tooltipContext.setOpenDataPointId(item.datapointid);
      });

      const onMobile = mobileDetected();

      if (onMobile) {
         // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
         onClose();
      }
   };

   const sku = fromWiki(item, 'sku');
   const name = fromWiki(item, 'name') || item.description;

   return (
      <button
         onClick={onClick}
         className="flex flex-col flex-wrap whitespace-normal w-full py-3 px-4 text-gray-800 leading-normal no-underline font-normal rounded-none items-start text-left hover:bg-gray-200 focus:bg-gray-200 focus:outline-none"
      >
         <span className="text-base text-gray-800">{name}</span>
         {sku && <span className="text-sm text-gray-600">{sku}</span>}
      </button>
   );
}

const registerShortCuts = (
   isOpen: MinimalDisclosure['isOpen'],
   onClose: MinimalDisclosure['onClose']
) => {
   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useContext" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   const keypressContext = useContext(KeypressContext);

   // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook "useEffect" is called in "registerShortCuts" that is neither a React function component nor a custom Hook ...
   useEffect(() => {
      const mappings = [{ id: 'PartsList', key: 'Escape', action: onClose }];

      if (isOpen) {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.pushMappings(mappings);
      } else {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.popMappings(mappings);
      }

      return () => {
         // @ts-expect-error TS(2345) FIXME: Argument of type '{ id: string; key: string; actio... Remove this comment to see the full error message
         keypressContext.popMappings(mappings);
      };
   }, [isOpen]);
};

const getPartRow = (onClose: MinimalDisclosure['onClose']) =>
   memo(function getPartRowInternal({ datapoint }: { datapoint: iFixitHotSpotItem }) {
      return <Part item={datapoint} onClose={onClose} />;
   });

function DiagramSettings() {
   const { dotsVisible, setDotsVisible } = useDotsVisibility();

   return (
      <div className="px-4 pt-4 pb-3">
         <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
            {_js('Settings')}
         </p>
         <div className="mt-2 flex items-center justify-between gap-2">
            <Label htmlFor="diagram-show-dots" className="text-sm font-normal text-gray-800">
               {_js('Show dots')}
            </Label>
            <Switch id="diagram-show-dots" checked={dotsVisible} onCheckedChange={setDotsVisible} />
         </div>
      </div>
   );
}

function FloatingPartsList() {
   const topMargin = '110px';
   const bottomMargin = '32px';
   const contentMargin = '32px';
   const widthMargin = '32px';

   const searchContext = useSearchContext<iFixitHotSpotItem>();
   const searchText = searchContext.searchText;
   const { isOpen, onClose } = searchContext.disclosure;
   const filteredData = searchContext.filteredData;

   registerShortCuts(isOpen, onClose);

   const onChange: ChangeEventHandler<HTMLInputElement> = event => {
      searchContext.setSearchText(event.target.value);
   };

   const PartRow = useMemo(() => getPartRow(onClose), [onClose]);

   if (!isOpen) {
      return null;
   }

   return (
      <PartListContainer marginRight={widthMargin} marginTop={topMargin} width="300px">
         <DiagramSettings />
         <div className="border-t border-gray-300" />
         <div className="m-4">
            <SearchInput value={searchText} onChange={onChange} />
         </div>
         <PartsList
            data={filteredData}
            PartRow={PartRow}
            maxHeight={`calc(100vh - ${topMargin} -  (2 * ${bottomMargin}) - ${contentMargin})`}
         />
      </PartListContainer>
   );
}

export default memo(FloatingPartsList);
